import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Ticket, Pencil } from 'lucide-react';

const EMPTY = { code: '', access_level: 'regen_method', durationMode: '12', access_expiry_date: '', usage_limit: 100, expires_at: '', program: '' };
const LEVEL_LABEL = { regen_method: 'Full YourReGen Protocol', regen_plus: 'YourReGen+' };

export default function AdminAccessCodes() {
  const [codes, setCodes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // code id or null
  const [f, setF] = useState(EMPTY);

  const load = async () => { const r = await api.get('/access-codes'); setCodes(r.data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setF(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c.id);
    setF({
      code: c.code, access_level: c.access_level || 'regen_method',
      durationMode: c.access_expiry_date ? 'custom' : String(c.access_duration_months || '12'),
      access_expiry_date: c.access_expiry_date || '',
      usage_limit: c.usage_limit ?? 100, expires_at: c.expires_at || '', program: c.program || '',
    });
    setOpen(true);
  };

  const save = async () => {
    if (!f.code.trim()) { toast.error('Enter a code'); return; }
    const payload = {
      code: f.code.trim().toUpperCase(), access_level: f.access_level, usage_limit: parseInt(f.usage_limit) || 100,
      expires_at: f.expires_at || '', program: (f.program || '').toUpperCase(), discount_pct: 0, free_access: true,
    };
    if (f.durationMode === 'custom') {
      if (!f.access_expiry_date) { toast.error('Pick a custom expiry date'); return; }
      payload.access_expiry_date = f.access_expiry_date; payload.access_duration_months = null;
    } else {
      payload.access_duration_months = parseInt(f.durationMode); payload.access_expiry_date = null;
    }
    try {
      if (editing) await api.put(`/access-codes/${editing}`, payload);
      else await api.post('/access-codes', payload);
      setOpen(false); load(); toast.success(editing ? 'Updated' : 'Created');
    } catch { toast.error('Failed'); }
  };

  const del = async (id) => { if (!confirm('Delete?')) return; await api.delete(`/access-codes/${id}`); load(); };

  const entitlement = (c) => c.access_expiry_date ? `until ${c.access_expiry_date}` : (c.access_duration_months ? `${c.access_duration_months} months` : 'no expiry set');

  return (
    <div className="space-y-6" data-testid="admin-codes">
      <div className="flex items-end justify-between">
        <div><div className="label-mini text-[#FFB800]">Access</div><h1 className="font-display font-black text-4xl mt-1">Access Codes</h1></div>
        <Button data-testid="add-code-btn" onClick={openNew} className="bg-[#FFB800] text-[#081726] font-semibold"><Plus className="w-4 h-4 mr-2" /> New Code</Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)]">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Access Code</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Code</Label><Input value={f.code} onChange={e => setF({ ...f, code: e.target.value.toUpperCase() })} data-testid="code-name" className="bg-transparent border-[var(--c-hair)] uppercase font-mono" /></div>
            <div>
              <Label>Access Level</Label>
              <Select value={f.access_level} onValueChange={v => setF({ ...f, access_level: v })}>
                <SelectTrigger className="bg-transparent border-[var(--c-hair)]" data-testid="code-access-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="regen_method">Full YourReGen Protocol (coaching + program)</SelectItem>
                  <SelectItem value="regen_plus">YourReGen+ (program + tracking)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Access Duration <span className="text-xs text-muted-foreground font-normal">(starts when the client activates the code)</span></Label>
              <Select value={f.durationMode} onValueChange={v => setF({ ...f, durationMode: v })}>
                <SelectTrigger className="bg-transparent border-[var(--c-hair)]" data-testid="code-duration"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 month</SelectItem>
                  <SelectItem value="3">3 months</SelectItem>
                  <SelectItem value="6">6 months</SelectItem>
                  <SelectItem value="12">12 months</SelectItem>
                  <SelectItem value="custom">Custom expiry date</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {f.durationMode === 'custom' && (
              <div><Label>Custom Expiry Date</Label><Input type="date" value={f.access_expiry_date} onChange={e => setF({ ...f, access_expiry_date: e.target.value })} data-testid="code-expiry-date" className="bg-transparent border-[var(--c-hair)]" /></div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Usage Limit</Label><Input type="number" value={f.usage_limit} onChange={e => setF({ ...f, usage_limit: parseInt(e.target.value) || 0 })} data-testid="code-usage-limit" className="bg-transparent border-[var(--c-hair)] font-mono" /></div>
              <div><Label>Code valid until <span className="text-xs text-muted-foreground font-normal">(optional)</span></Label><Input type="date" value={f.expires_at} onChange={e => setF({ ...f, expires_at: e.target.value })} className="bg-transparent border-[var(--c-hair)]" /></div>
            </div>
            <div><Label>Grants Program <span className="text-xs text-muted-foreground font-normal">(optional label)</span></Label><Input value={f.program} onChange={e => setF({ ...f, program: e.target.value.toUpperCase() })} placeholder="YOURREGEN12" data-testid="code-program" className="bg-transparent border-[var(--c-hair)] uppercase font-mono" /></div>
            <Button onClick={save} data-testid="code-save" className="w-full bg-[#FFB800] text-[#081726] font-semibold">{editing ? 'Save Changes' : 'Create'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {codes.map(c => (
          <div key={c.id} className="regen-card-elevated p-5 group" data-testid={`code-${c.id}`}>
            <div className="flex items-start justify-between">
              <Ticket className="w-5 h-5 text-[#FFB800]" />
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`code-edit-${c.id}`}><Pencil className="w-3 h-3 text-[#00C8FF]" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(c.id)}><Trash2 className="w-3 h-3 text-red-400" /></Button>
              </div>
            </div>
            <div className="font-mono text-2xl mt-3 text-[#FFB800]">{c.code}</div>
            <div className="mt-2 pill text-[10px] bg-[#00C8FF]/10 text-[#00C8FF]">{LEVEL_LABEL[c.access_level] || 'Full YourReGen Protocol'}</div>
            <div className="text-xs text-muted-foreground mt-2">Access: {entitlement(c)} · {c.uses}/{c.usage_limit} used</div>
            {c.expires_at && <div className="text-xs text-muted-foreground">Code valid until {c.expires_at}</div>}
          </div>
        ))}
        {codes.length === 0 && <div className="col-span-full text-center text-muted-foreground text-sm py-10">No codes yet.</div>}
      </div>
    </div>
  );
}
